import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useAlerts } from "context/AlertContext";
import { IStructuredTemplate } from "models/structuredTemplates.models";
import { services } from "services";
import { AlertSeverity } from "utils/constants/alerts.constants";
import { QueryAPIKey } from "utils/constants/api.constants";
import handleRetryFailure from "utils/helpers/handleRetryFailure";
import { TErrorData, TReactQueryObserverOptions } from "utils/types";

export default function useStructuredTemplate(
  id: string,
  projectId: string,
  options?: TReactQueryObserverOptions<
    AxiosResponse<IStructuredTemplate>,
    AxiosError<TErrorData>,
    IStructuredTemplate
  >,
) {
  const { t } = useTranslation("common", {
    keyPrefix: "notifications",
  });

  const { showAlert } = useAlerts();

  const { data, error, isLoading, isFetching, isError, isSuccess } = useQuery<
    AxiosResponse<IStructuredTemplate>,
    AxiosError<TErrorData>,
    IStructuredTemplate
  >({
    queryKey: [QueryAPIKey.GetStructuredTemplate, id],
    queryFn: () =>
      services.structuredTemplates.getStructuredTemplate(id, projectId),
    retry: handleRetryFailure(() =>
      showAlert({
        message: t("structuredTemplateLoadFailure"),
        severity: AlertSeverity.Error,
      }),
    ),
    select: ({ data }) => data,
    ...options,
  });

  return {
    structuredTemplate: data,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
  };
}
